<script setup>
import { computed, ref, watch } from 'vue';
import { useToggle } from '@vueuse/core';
import { useI18n } from 'vue-i18n';
import addDays from 'date-fns/addDays';
import addMonths from 'date-fns/addMonths';
import differenceInCalendarDays from 'date-fns/differenceInCalendarDays';
import endOfDay from 'date-fns/endOfDay';
import endOfMonth from 'date-fns/endOfMonth';
import format from 'date-fns/format';
import isValid from 'date-fns/isValid';
import parseISO from 'date-fns/parseISO';
import startOfDay from 'date-fns/startOfDay';
import startOfMonth from 'date-fns/startOfMonth';
import subDays from 'date-fns/subDays';
import { vOnClickOutside } from '@vueuse/components';
import Button from 'dashboard/components-next/button/Button.vue';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';

const emit = defineEmits(['rangeTypeChange', 'monthOffsetChange']);

const fromModel = defineModel('from', { type: Date, default: null });
const toModel = defineModel('to', { type: Date, default: null });
const daysNumModel = defineModel('daysNum', { type: Number, default: null });

const { t, locale } = useI18n();

const DATE_FILTER_TYPES = {
  DAY: 'day',
  MONTH: 'month',
  CUSTOM: 'custom',
};

// PERÍODO ESCOLHIDO À MÃO (pedido do clube, 15/10: "na aba visão geral / ao vivo inserir uma
// opção de filtro para buscarmos a data inicial").
//
// As opções prontas são todas relativas a hoje: últimos 7, 14, 30 dias, este mês. Nenhuma
// responde "quero ver o dia 12", que é o que a operação pergunta quando vai conferir um teste ou
// um jogo. A escolha à mão vive no rodapé do mesmo menu, do jeito que já funciona na busca de
// conversas, para não inventar um segundo desenho para a mesma coisa.
//
// ⚠️ TETO DE 31 DIAS: o mapa desenha uma linha por dia e a consulta devolve hora a hora. Sem
// teto, um ano de intervalo vira 365 linhas e quase nove mil pontos numa tela só. Quem pedir
// mais recebe o início que escolheu e o fim recuado, com o aviso à vista.
const TETO_DE_DIAS = 31;

const comoCampo = data => format(data, 'yyyy-MM-dd');

const inicioEscolhido = ref(comoCampo(subDays(new Date(), 6)));
const fimEscolhido = ref(comoCampo(new Date()));
const hojeNoCampo = comoCampo(new Date());
// Preenchido só depois do "Aplicar": é ele que diz que o período da tela é o escolhido à mão.
const periodoEscolhido = ref(null);

const DATE_FILTER_ACTION = 'select_date_range';

const dayMenuItemConfigs = computed(() => [
  {
    label: t('REPORT.DATE_RANGE_OPTIONS.LAST_7_DAYS'),
    value: 'last_7_days',
    action: DATE_FILTER_ACTION,
    type: DATE_FILTER_TYPES.DAY,
    daysBefore: 6,
  },
  {
    label: t('REPORT.DATE_RANGE_OPTIONS.LAST_14_DAYS'),
    value: 'last_14_days',
    action: DATE_FILTER_ACTION,
    type: DATE_FILTER_TYPES.DAY,
    daysBefore: 13,
  },
  {
    label: t('REPORT.DATE_RANGE_OPTIONS.LAST_30_DAYS'),
    value: 'last_30_days',
    action: DATE_FILTER_ACTION,
    type: DATE_FILTER_TYPES.DAY,
    daysBefore: 29,
  },
]);

const resolvedLocale = computed(() => {
  const currentLocale =
    locale.value ||
    (typeof navigator !== 'undefined' ? navigator.language : 'en');
  return currentLocale.replace('_', '-');
});

const monthFormatter = computed(
  () =>
    new Intl.DateTimeFormat(resolvedLocale.value, {
      month: 'long',
      year: 'numeric',
    })
);

const monthMenuItemConfigs = computed(() => {
  const now = new Date();
  const offsets = [0, -1, -2];

  return offsets.map(offset => ({
    label:
      offset === 0
        ? t('REPORT.DATE_RANGE_OPTIONS.THIS_MONTH')
        : monthFormatter.value.format(addMonths(now, offset)),
    value: offset === 0 ? 'this_month' : `month_${offset}`,
    action: DATE_FILTER_ACTION,
    type: DATE_FILTER_TYPES.MONTH,
    monthOffset: offset,
  }));
});

const selectedDateRangeValue = ref('');

const [showDropdown, toggleDropdown] = useToggle();
const monthOffset = ref(0);

const menuItems = computed(() => {
  const selectedValue = selectedDateRangeValue.value;
  return [...dayMenuItemConfigs.value, ...monthMenuItemConfigs.value].map(
    config => ({
      ...config,
      isSelected: selectedValue === config.value,
    })
  );
});

selectedDateRangeValue.value = menuItems.value[0]?.value || '';

const menuSections = computed(() => {
  const dayItems = menuItems.value.filter(
    item => item.type === DATE_FILTER_TYPES.DAY
  );
  const monthItems = menuItems.value.filter(
    item => item.type === DATE_FILTER_TYPES.MONTH
  );

  return [{ items: dayItems }, { items: monthItems }].filter(
    section => section.items.length > 0
  );
});

const selectedConfig = computed(
  () =>
    menuItems.value.find(
      menuItem => menuItem.value === selectedDateRangeValue.value
    ) || menuItems.value[0]
);

const selectedLabel = computed(() => {
  if (periodoEscolhido.value) {
    const { from, to } = periodoEscolhido.value;
    return `${format(from, 'dd/MM/yy')} a ${format(to, 'dd/MM/yy')}`;
  }
  const selectedItem = menuItems.value.find(
    item => item.value === selectedDateRangeValue.value
  );
  return selectedItem?.label || '';
});

const computeRange = config => {
  if (!config) {
    return null;
  }

  if (config.type === DATE_FILTER_TYPES.MONTH) {
    const now = new Date();
    const baseMonthStart = startOfMonth(addMonths(now, monthOffset.value));
    const from = startOfDay(baseMonthStart);
    const isCurrentMonth =
      config.value === 'this_month' && monthOffset.value === 0;
    const to = isCurrentMonth
      ? endOfDay(now)
      : endOfDay(endOfMonth(baseMonthStart));
    const daysBefore = differenceInCalendarDays(to, from);
    return { from, to, daysBefore };
  }

  const to = endOfDay(new Date());
  const from = startOfDay(subDays(to, Number(config.daysBefore)));
  return { from, to, daysBefore: Number(config.daysBefore) };
};

const applySelection = config => {
  if (!config) return;

  if (config.type === DATE_FILTER_TYPES.MONTH) {
    monthOffset.value = config.monthOffset || 0;
  } else {
    monthOffset.value = 0;
  }

  const range = computeRange(config);
  if (!range) return;

  const { from, to, daysBefore } = range;
  fromModel.value = from;
  toModel.value = to;
  daysNumModel.value = daysBefore;

  emit('rangeTypeChange', config.type);
  emit('monthOffsetChange', monthOffset.value);
};

const aplicarPeriodoEscolhido = () => {
  if (!inicioEscolhido.value || !fimEscolhido.value) return;

  let from = startOfDay(parseISO(inicioEscolhido.value));
  let to = endOfDay(parseISO(fimEscolhido.value));
  if (!isValid(from) || !isValid(to)) return;

  // Datas trocadas acontecem, e recusar em silêncio seria pior do que entender o pedido.
  if (from > to) {
    const guardado = from;
    from = startOfDay(to);
    to = endOfDay(guardado);
  }

  if (differenceInCalendarDays(to, from) > TETO_DE_DIAS - 1) {
    to = endOfDay(addDays(from, TETO_DE_DIAS - 1));
    fimEscolhido.value = comoCampo(to);
  }

  periodoEscolhido.value = { from, to };
  fromModel.value = from;
  toModel.value = to;
  // Sem dias relativos: é isto que faz o painel usar o intervalo fixo em vez de recalcular a
  // janela a partir de agora a cada atualização automática.
  daysNumModel.value = null;

  emit('rangeTypeChange', DATE_FILTER_TYPES.CUSTOM);
  emit('monthOffsetChange', 0);
  toggleDropdown(false);
};

const handleAction = ({ action, value }) => {
  toggleDropdown(false);
  if (action !== DATE_FILTER_ACTION) {
    return;
  }
  // Escolher uma opção pronta desfaz o período à mão. Aplicar aqui, e não só confiar no
  // observador abaixo, cobre o caso de reescolher a opção que já estava marcada.
  periodoEscolhido.value = null;
  selectedDateRangeValue.value = value;
  applySelection(selectedConfig.value);
};

watch(
  () => selectedConfig.value,
  config => {
    if (periodoEscolhido.value) return;
    applySelection(config);
  },
  { immediate: true }
);
</script>

<template>
  <div
    v-on-click-outside="() => toggleDropdown(false)"
    class="relative flex items-center group"
  >
    <Button
      sm
      slate
      faded
      :label="selectedLabel"
      class="rounded-md group-hover:bg-n-alpha-2"
      @click="toggleDropdown()"
    />
    <DropdownMenu
      v-if="showDropdown"
      :menu-items="menuItems"
      :menu-sections="menuSections"
      class="mt-1 ltr:right-0 rtl:left-0 xl:ltr:right-0 xl:rtl:left-0 top-full w-64"
      @action="handleAction($event)"
    >
      <template #footer>
        <div class="h-px bg-n-strong" />
        <div class="flex flex-col gap-2 px-2 py-2">
          <span class="px-1 text-sm text-n-slate-11">
            {{ t('REPORT.HEATMAP_PERIODO.TITULO') }}
          </span>
          <label class="flex flex-col gap-1 px-1 mb-0 text-xs text-n-slate-11">
            {{ t('REPORT.HEATMAP_PERIODO.DE') }}
            <input
              v-model="inicioEscolhido"
              type="date"
              :max="fimEscolhido || hojeNoCampo"
              class="!w-full !mb-0 !rounded-lg !bg-n-alpha-black2 !outline-n-strong -outline-offset-1 !px-3 !py-2 !text-sm text-n-slate-12 !h-8"
            />
          </label>
          <label class="flex flex-col gap-1 px-1 mb-0 text-xs text-n-slate-11">
            {{ t('REPORT.HEATMAP_PERIODO.ATE') }}
            <input
              v-model="fimEscolhido"
              type="date"
              :min="inicioEscolhido"
              :max="hojeNoCampo"
              class="!w-full !mb-0 !rounded-lg !bg-n-alpha-black2 !outline-n-strong -outline-offset-1 !px-3 !py-2 !text-sm text-n-slate-12 !h-8"
            />
          </label>
          <p class="px-1 mb-0 text-xs text-n-slate-10">
            {{ t('REPORT.HEATMAP_PERIODO.TETO') }}
          </p>
          <Button
            sm
            :label="t('REPORT.CUSTOM_DATE_RANGE.CONFIRM')"
            class="justify-center"
            @click="aplicarPeriodoEscolhido"
          />
        </div>
      </template>
    </DropdownMenu>
  </div>
</template>
